import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Crown, Briefcase, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { useAuth, homeFor } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";


export const PORTALS = [
  { key: "owner", label: "Owner", icon: Crown, hint: "Business owner — full control" },
  { key: "manager", label: "Manager", icon: Briefcase, hint: "Branch managers" },
  { key: "staff", label: "Staff", icon: Users, hint: "Pharmacists, cashiers, inventory officers" },
];

export default function Login() {
  const { session, login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const portal = PORTALS.some((p) => p.key === params.get("as")) ? params.get("as") : "staff";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const next = params.get("next");
  const dest = (s) => (next && /^\/app\/[\w\-/]*$/.test(next) ? next : homeFor(s));
  if (session) return <Navigate to={dest(session)} replace />;

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const s = await login(email, password, portal); if (s.mfa_required) { setMfa(s); setPassword(""); } else nav(dest(s)); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const submitCode = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const s = await verifyMfa(mfa.challenge_id, code); nav(dest(s)); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const resend = async () => {
    setErr("");
    try { const r = await api.post("/auth/mfa/resend", { challenge_id: mfa.challenge_id }); setMfa({ ...mfa, delivery: r.data.delivery }); } catch (ex) { setErr(errMsg(ex)); }
  };
  const b = cfg?.branding;
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[hsl(var(--navy))] p-12 text-white relative overflow-hidden">
        <img src="/brand/shelves.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative flex items-center gap-3">
          <span className="h-12 w-12 rounded-xl bg-white grid place-items-center"><img src={b?.logo_url || "/brand/logo-square.png"} alt="" className="h-10" /></span>
          <span className="font-display font-bold text-lg">{b?.name || "Everett Pharmacy Ltd"}</span>
        </div>
        <div className="relative">
          <p className="text-4xl font-display font-extrabold leading-tight max-w-md">Pharmacy operations you can audit.</p>
          <p className="mt-4 text-slate-300 max-w-md">Sign in with the account your pharmacy owner invited. Access is limited to your role and branch.</p>
        </div>
        <p className="relative text-xs text-slate-400">Everett Pharmacy Ltd · Pharmacy management</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        {mfa ? (
          <form onSubmit={submitCode} className="w-full max-w-sm space-y-5" data-testid="mfa-form">
            <div><h1 className="text-3xl font-bold">Enter sign-in code</h1>
              <p className="text-sm text-muted-foreground mt-1" data-testid="mfa-destination">We sent a 6-digit code to {mfa.destination}. It expires in 10 minutes.</p></div>
            {mfa.delivery !== "sent" && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="mfa-delivery-warning">The code email could not be sent ({mfa.delivery}). Try resending.</p>}
            <Field label="Code"><Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required data-testid="mfa-code-input" /></Field>
            {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">{err}</p>}
            <Button type="submit" className="w-full h-11" disabled={busy || code.length !== 6} data-testid="mfa-submit-button">{busy && <Loader2 className="animate-spin mr-2" size={16} />}Verify and sign in</Button>
            <div className="flex justify-between text-sm">
              <button type="button" onClick={resend} className="text-secondary hover:underline" data-testid="mfa-resend-button">Resend code</button>
              <button type="button" onClick={() => { setMfa(null); setCode(""); setErr(""); }} className="text-muted-foreground hover:underline" data-testid="mfa-back-button">Back</button>
            </div>
          </form>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex justify-center mb-2"><img src={b?.logo_url || "/brand/logo-square.png"} alt="logo" className="h-20" /></div>
          <div><h1 className="text-3xl font-bold">Sign in</h1><p className="text-sm text-muted-foreground mt-1">{PORTALS.find((p) => p.key === portal).hint}</p></div>
          <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-slate-100" role="tablist" data-testid="portal-tabs">
            {PORTALS.map((p) => <button key={p.key} type="button" role="tab" aria-selected={portal === p.key} onClick={() => { setParams({ as: p.key }); setErr(""); }} data-testid={`portal-tab-${p.key}`}
              className={`flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-semibold transition-colors ${portal === p.key ? "bg-white shadow text-primary" : "text-slate-500 hover:text-slate-800"}`}><p.icon size={15} />{p.label}</button>)}
          </div>
          <Field label="Username or email"><Input type="text" autoComplete="username" autoCapitalize="none" placeholder="firstname.lastname@staff.everettpharmacy.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" /></Field>
          <Field label="Password"><Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" /></Field>
          <div className="text-right -mt-2"><Link to="/forgot-password" className="text-sm text-secondary hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
          {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">{err}</p>}
          <Button type="submit" className="w-full h-11" disabled={busy} data-testid="login-submit-button">{busy && <Loader2 className="animate-spin mr-2" size={16} />}Sign in as {PORTALS.find((p) => p.key === portal).label}</Button>
          {cfg?.demo_mode && cfg.demo_accounts?.length > 0 && (
            <div className="pt-4 border-t" data-testid="demo-accounts">
              <p className="text-xs font-semibold text-yellow-800 mb-2">Demo environment — synthetic accounts (hidden in production)</p>
              <div className="grid grid-cols-2 gap-2">
                {cfg.demo_accounts.map((a) => (
                  <button type="button" key={a.email} onClick={() => { setEmail(a.email); setPassword(a.password); }} className="text-left text-xs rounded-lg border px-3 py-2 hover:bg-muted"
                    data-testid={`demo-fill-${a.label.toLowerCase()}`}>{a.label}</button>
                ))}
              </div>
            </div>
          )}
        </form>
        )}
      </div>
    </div>
  );
}
